import { useEffect, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Calendar,
  CalendarDays,
  ClipboardList,
  Clock,
  FileBarChart2,
  FileSpreadsheet,
  FileText,
  Fuel,
  PlayCircle,
  Truck,
  Wallet,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { reportsApi, type ReportRun } from '@/features/financial/api';
import { PageHeader, SectionLabel, StatusPill } from '@/lib/ui';

type ReportMeta = {
  label: string;
  needsDates: boolean;
  needsPeriod?: boolean;
  daysAhead?: boolean;
  icon: LucideIcon;
  description: string;
};

const REPORT_INFO: Record<string, ReportMeta> = {
  asset_utilization: {
    label: 'Asset Utilization',
    needsDates: true,
    icon: Truck,
    description: 'Per-asset active vs. idle hours and distance.',
  },
  fuel_summary: {
    label: 'Fuel Summary',
    needsDates: true,
    icon: Fuel,
    description: 'Fuel volume, cost, and km / L by asset.',
  },
  payroll_register: {
    label: 'Payroll Register (WPS)',
    needsDates: false,
    needsPeriod: true,
    icon: Wallet,
    description: 'WPS-format register for a payroll period.',
  },
  attendance_summary: {
    label: 'Attendance Summary',
    needsDates: true,
    icon: CalendarDays,
    description: 'Worked vs. expected hours, lateness, gaps.',
  },
  work_unit_log: {
    label: 'Work Unit Log',
    needsDates: true,
    icon: ClipboardList,
    description: 'Engagement work units and billable totals.',
  },
  engagement_pnl: {
    label: 'Engagement P&L',
    needsDates: true,
    icon: BarChart3,
    description: 'Per-engagement revenue, cost, and margin.',
  },
  document_expiry: {
    label: 'Document Expiry',
    needsDates: false,
    daysAhead: true,
    icon: AlertTriangle,
    description: 'Documents expiring within the next N days.',
  },
  maintenance_cost: {
    label: 'Maintenance Cost',
    needsDates: true,
    icon: Wrench,
    description: 'Service costs and downtime by asset.',
  },
};

const fallbackIcon: LucideIcon = FileText;

export function ReportsPage() {
  const [catalog, setCatalog] = useState<string[]>([]);
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [composer, setComposer] = useState<string | null>(null);

  const load = async () => {
    try {
      const [cat, rs] = await Promise.all([reportsApi.catalog(), reportsApi.list({ limit: 50 })]);
      setCatalog(cat);
      setRuns(rs);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void load();
    const interval = setInterval(() => {
      if (runs.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING')) {
        void load();
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runNow = async (id: string, name: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { blob, rowCount } = await reportsApi.runNow(id);
      if (rowCount === 0) {
        setNotice(
          `No data found for this range — "${REPORT_INFO[name]?.label ?? name}" produced 0 rows. Nothing to download.`,
        );
        await load();
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}-${id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Reports"
        icon={FileBarChart2}
        subtitle="Export console. Run any of the 8 standard reports; downloads stream as CSV."
      />

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{notice}</span>
          </div>
          <button
            className="text-xs underline opacity-80 hover:opacity-100"
            onClick={() => setNotice(null)}
          >
            dismiss
          </button>
        </div>
      )}

      <section className="mb-8">
        <SectionLabel icon={FileSpreadsheet}>Available reports</SectionLabel>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {catalog.map((name) => {
            const meta = REPORT_INFO[name];
            const Icon = meta?.icon ?? fallbackIcon;
            return (
              <button
                key={name}
                className="card-interactive group flex flex-col items-start gap-2 text-left"
                onClick={() => setComposer(name)}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/25 transition-colors group-hover:bg-primary/20">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="font-semibold leading-tight text-foreground">
                    {meta?.label ?? name}
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-muted-foreground">{name}</div>
                </div>
                {meta?.description && (
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {meta.description}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <SectionLabel icon={Clock} aside={`${runs.length} run${runs.length === 1 ? '' : 's'}`}>
          Recent runs
        </SectionLabel>
        <div className="data-table-wrapper">
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Report</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th className="num">Rows</th>
                  <th className="num">Size</th>
                  <th className="num"></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const meta = REPORT_INFO[r.name];
                  const Icon = meta?.icon ?? fallbackIcon;
                  return (
                    <tr key={r.id}>
                      <td>
                        <div className="flex items-start gap-2.5">
                          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-foreground">
                              {meta?.label ?? r.name}
                            </div>
                            <div className="font-mono text-[11px] text-muted-foreground">
                              {r.id.slice(0, 8)}…
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="muted text-xs">{new Date(r.created_at).toLocaleString()}</td>
                      <td>
                        <StatusPill status={r.status} />
                        {r.error_message && (
                          <div className="mt-1 text-xs text-danger">{r.error_message}</div>
                        )}
                      </td>
                      <td className="num">{r.row_count ?? '—'}</td>
                      <td className="num">
                        {r.byte_size ? `${(r.byte_size / 1024).toFixed(1)} KB` : '—'}
                      </td>
                      <td className="actions">
                        {(r.status === 'QUEUED' || r.status === 'FAILED') && (
                          <button
                            className="link text-xs"
                            disabled={busy}
                            onClick={() => runNow(r.id, r.name)}
                          >
                            <PlayCircle className="h-3.5 w-3.5" />
                            Run now
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {runs.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-sm text-muted-foreground"
                    >
                      No runs yet. Click a report card above to compose one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {composer && (
        <ComposerModal
          name={composer}
          onCancel={() => setComposer(null)}
          onStarted={() => {
            setComposer(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function ComposerModal({
  name,
  onCancel,
  onStarted,
}: {
  name: string;
  onCancel: () => void;
  onStarted: () => void;
}) {
  const info = REPORT_INFO[name];
  const Icon = info?.icon ?? fallbackIcon;
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo());
  const [dateTo, setDateTo] = useState(today());
  const [period, setPeriod] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
  );
  const [daysAhead, setDaysAhead] = useState('60');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const params: Record<string, unknown> = {};
      if (info?.needsDates) {
        params.date_from = new Date(dateFrom).toISOString();
        params.date_to = new Date(dateTo + 'T23:59:59').toISOString();
      }
      if (info?.needsPeriod) params.period = period;
      if (info?.daysAhead) params.days_ahead = Number(daysAhead);
      await reportsApi.start(name, params);
      onStarted();
    } catch (e) {
      setErr((e as Error).message || 'Start failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal">
        <header className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/25">
              <Icon className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-lg font-semibold leading-tight">{info?.label ?? name}</h3>
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{name}</p>
            </div>
          </div>
          <button className="btn-ghost btn-sm -m-1" onClick={onCancel} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        {err && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        <div className="space-y-3">
          {info?.needsDates && (
            <>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  From
                </label>
                <div className="relative">
                  <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="date"
                    className="input pl-9"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  To
                </label>
                <div className="relative">
                  <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="date"
                    className="input pl-9"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
          {info?.needsPeriod && (
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Period (YYYY-MM)
              </label>
              <input
                className="input font-mono"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              />
            </div>
          )}
          {info?.daysAhead && (
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Days ahead
              </label>
              <input
                className="input font-mono"
                type="number"
                value={daysAhead}
                onChange={(e) => setDaysAhead(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            <PlayCircle className="h-4 w-4" />
            {busy ? 'Queuing…' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function thirtyDaysAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}
